// leadRoutingLogic.ts — Lead Router v2 (Wave 3 / S6) PURE UI logic.
// Three tested units: M1 cell classifier, M4c tri-state district tree, M4 wizard
// save-payload builder. No React, no network — unit-tested in leadRoutingLogic.test.tsx.

import type {
  CoverageCellV2,
  AssignmentInputV2,
  RouteLayerV2,
} from '@/api/leadRouter';

// ── M1 Coverage-matrix cell classifier ──────────────────────────────────────
// Maps a coverage cell to a visual tone + label + destination, matching the
// mockup legend: green owned / red gap→fallback / amber exec-overlap /
// violet manager-overlap→triage / saffron dealer dot.
export type CellTone = 'owned' | 'gap' | 'overlap' | 'triage';

export interface CellView {
  tone: CellTone;
  label: string;
  destination: CoverageCellV2['destination'];
  dealerDot: boolean;
}

export function classifyMatrixCell(cell: CoverageCellV2): CellView {
  // Priority mirrors the resolver's precedence: overlaps first (they force triage),
  // then owned, then gap. A manager-overlap is the violet "→ triage" case.
  let tone: CellTone;
  let label: string;
  if (cell.managerOverlap) {
    tone = 'triage';
    label = 'Mgr-overlap → triage';
  } else if (cell.execOverlap) {
    tone = 'overlap';
    label = 'Exec-overlap · pick primary';
  } else if (cell.owned) {
    tone = 'owned';
    label = cell.dealer ? 'Owned + dealer' : 'Owned';
  } else {
    tone = 'gap';
    label = 'Gap → Fallback';
  }
  return { tone, label, destination: cell.destination, dealerDot: cell.dealer };
}

// ── M4c States → Districts tri-state tree ────────────────────────────────────
// Selection is a Set of "State::District" keys. A state header reflects the
// tri-state of its districts.
export type TriState = 'all' | 'some' | 'none';

export const districtKey = (state: string, district: string): string => `${state}::${district}`;

export function stateTriState(
  state: string,
  allDistricts: string[],
  selected: ReadonlySet<string>,
): TriState {
  if (allDistricts.length === 0) return 'none';
  let on = 0;
  for (const d of allDistricts) if (selected.has(districtKey(state, d))) on++;
  if (on === 0) return 'none';
  if (on === allDistricts.length) return 'all';
  return 'some';
}

/** Toggle a whole state: if every district is on → clear them; else select all. */
export function toggleState(
  state: string,
  allDistricts: string[],
  selected: ReadonlySet<string>,
): Set<string> {
  const next = new Set(selected);
  const tri = stateTriState(state, allDistricts, selected);
  if (tri === 'all') {
    for (const d of allDistricts) next.delete(districtKey(state, d));
  } else {
    for (const d of allDistricts) next.add(districtKey(state, d));
  }
  return next;
}

export function toggleDistrict(
  state: string,
  district: string,
  selected: ReadonlySet<string>,
): Set<string> {
  const next = new Set(selected);
  const k = districtKey(state, district);
  if (next.has(k)) next.delete(k);
  else next.add(k);
  return next;
}

/** Merge a "+100 km" expansion (S9) into the current selection (additive). */
export function applyRadiusExpansion(
  districts: Array<{ state: string; district: string }>,
  selected: ReadonlySet<string>,
): Set<string> {
  const next = new Set(selected);
  for (const d of districts) next.add(districtKey(d.state, d.district));
  return next;
}

// ── M4 wizard save-payload builder ───────────────────────────────────────────
// Converts wizard state into the POST /assignments/bulk `assignments[]` body.
export interface CityRef { state?: string | null; city: string }

export interface TerritorySelection {
  districts: string[]; // "State::District" keys
  extraCities?: CityRef[]; // "+ Add a city not listed"
  denyCities?: CityRef[]; // custom-area carve-outs (state minus these cities)
}

export interface WizardState {
  layer: RouteLayerV2;
  userId?: string | null;
  dealerId?: string | null;
  mode: 'unified' | 'perprod';
  products: string[]; // product group keys
  unified?: TerritorySelection; // when mode==='unified'
  perProduct?: Record<string, TerritorySelection>; // when mode==='perprod'
}

const RANK: Record<string, number> = { pincode: 5, city: 10, district: 30, state: 50, catchall: 100 };

function rowsForTerritory(
  base: { layer: RouteLayerV2; userId?: string | null; dealerId?: string | null; productGroupKey: string | null },
  terr: TerritorySelection,
): AssignmentInputV2[] {
  const out: AssignmentInputV2[] = [];
  for (const key of terr.districts) {
    const district = key.split('::')[1] ?? key;
    out.push({ ...base, matchType: 'district', matchValue: district, rank: RANK.district });
  }
  for (const c of terr.extraCities ?? []) {
    out.push({ ...base, matchType: 'city', matchValue: c.city, rank: RANK.city });
  }
  for (const c of terr.denyCities ?? []) {
    out.push({ ...base, matchType: 'city', matchValue: c.city, deny: true, rank: RANK.city });
  }
  return out;
}

/**
 * Build the bulk-save assignments. Unified mode → productGroupKey=null (one
 * territory across every selected product). Per-product mode → one row-set per
 * product key. Deterministic ordering (no Date.now/Math.random).
 */
export function buildBulkPayload(w: WizardState): AssignmentInputV2[] {
  const base = { layer: w.layer, userId: w.userId ?? null, dealerId: w.dealerId ?? null };
  if (w.mode === 'unified') {
    const terr = w.unified ?? { districts: [] };
    return rowsForTerritory({ ...base, productGroupKey: null }, terr);
  }
  const out: AssignmentInputV2[] = [];
  for (const product of [...w.products].sort()) {
    const terr = w.perProduct?.[product] ?? { districts: [] };
    out.push(...rowsForTerritory({ ...base, productGroupKey: product }, terr));
  }
  return out;
}

/**
 * Which selected products have an EMPTY territory (Safety #1 red-dot in the
 * wizard tab list). Unified mode has a single shared territory, so it is either
 * all-empty or all-covered.
 */
export function emptyTerritoryProducts(w: WizardState): string[] {
  const isEmpty = (t?: TerritorySelection) =>
    !t || (t.districts.length === 0 && (t.extraCities?.length ?? 0) === 0);
  if (w.mode === 'unified') {
    return isEmpty(w.unified) ? [...w.products] : [];
  }
  return w.products.filter((p) => isEmpty(w.perProduct?.[p]));
}
